
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import EditUserForm from './EditUserForm';
import UsersListTable from './UsersListTable';
import { useUsersList } from './hooks/useUsersList';
import { toast } from '@/hooks/use-toast';

type UsersListProps = {
  refreshTrigger?: number;
  isLoading?: boolean;
};

const UsersList: React.FC<UsersListProps> = ({ refreshTrigger = 0, isLoading = false }) => {
  const {
    users,
    loading,
    isDeleting,
    fetchUsers,
    syncAllUsers,
    setAdminRole,
    toggleUserActive,
    deleteUser
  } = useUsersList(refreshTrigger);
  
  const [editingUser, setEditingUser] = useState<{ id: string; email: string } | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Ensure we fetch users when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchUsers();
    }
  }, [refreshTrigger]);

  const handleEditUser = (userId: string, email: string) => {
    setEditingUser({ id: userId, email });
  };

  const handleForceSync = async () => {
    try {
      setSyncing(true);
      await syncAllUsers();
      await fetchUsers();
      toast({
        title: "Synchronisation réussie",
        description: "La liste des utilisateurs a été synchronisée avec succès."
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la synchronisation.",
        variant: "destructive"
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-between mb-4 items-center">
        <p className="text-sm text-muted-foreground">
          {users.length} utilisateur{users.length !== 1 ? 's' : ''} trouvé{users.length !== 1 ? 's' : ''}
        </p>
        <div className="flex space-x-2">
          <Button 
            variant="outline"
            onClick={handleForceSync}
            disabled={syncing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Synchroniser les utilisateurs
          </Button>
          <Button 
            variant="outline"
            onClick={fetchUsers}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Rafraîchir la liste
          </Button>
        </div>
      </div>
      
      <UsersListTable 
        users={users}
        isDeleting={isDeleting}
        onEditUser={handleEditUser}
        onSetAdminRole={setAdminRole}
        onDeleteUser={deleteUser}
        onToggleActive={toggleUserActive}
      />

      {editingUser && (
        <EditUserForm
          userId={editingUser.id}
          email={editingUser.email}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onUserUpdated={fetchUsers}
        />
      )}
    </div>
  );
};

export default UsersList;
